import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';

export interface DiscountEligibility {
  eligible: boolean;
  amount: number;
  reason?: 'first_job' | 'rating_required' | 'already_used';
  message_he?: string;
  loading: boolean;
  error?: string;
}

const CACHE_KEY_PREFIX = 'discountEligibility-';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache

interface CachedEligibility {
  data: Omit<DiscountEligibility, 'loading'>;
  timestamp: number;
}

export const useDiscountEligibility = () => {
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState<DiscountEligibility>({
    eligible: false,
    amount: 0,
    loading: true
  });

  const checkEligibility = useCallback(async (forceRefresh = false) => {
    if (!user) {
      setEligibility({
        eligible: false,
        amount: 0,
        loading: false
      });
      return;
    }

    const cacheKey = `${CACHE_KEY_PREFIX}${user.id}`;

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedStr = localStorage.getItem(cacheKey);
      if (cachedStr) {
        try {
          const cached: CachedEligibility = JSON.parse(cachedStr);
          const isExpired = Date.now() - cached.timestamp > CACHE_TTL_MS;

          if (!isExpired) {
            console.log('Using cached discount eligibility:', cached.data);
            setEligibility({
              ...cached.data,
              loading: false
            });
            return;
          }
        } catch (e) {
          console.error('Error parsing cached eligibility:', e);
        }
      }
    }

    // Fetch from edge function
    try {
      setEligibility(prev => ({ ...prev, loading: true }));

      const { data, error } = await supabase.functions.invoke('check-discount-eligibility', {
        body: { user_id: user.id }
      });

      if (error) {
        console.error('Error checking discount eligibility:', error);
        setEligibility({
          eligible: false,
          amount: 0,
          loading: false,
          error: error.message
        });
        return;
      }

      console.log('Discount eligibility response:', data);

      const eligibilityData: Omit<DiscountEligibility, 'loading'> = {
        eligible: data.eligible || false,
        amount: data.amount || 0,
        reason: data.reason,
        message_he: data.message_he
      };

      // Cache the result
      const cacheData: CachedEligibility = {
        data: eligibilityData,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));

      setEligibility({
        ...eligibilityData,
        loading: false
      });
    } catch (err) {
      console.error('Unexpected error checking discount eligibility:', err);
      setEligibility({
        eligible: false,
        amount: 0,
        loading: false,
        error: 'Unexpected error'
      });
    }
  }, [user]);

  // Refresh eligibility (e.g., after rating a job)
  const refreshEligibility = useCallback(() => {
    checkEligibility(true);
  }, [checkEligibility]);

  // Clear cache (e.g., after accepting a quote with discount)
  const clearCache = useCallback(() => {
    if (user) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${user.id}`);
    }
  }, [user]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  return {
    ...eligibility,
    refreshEligibility,
    clearCache
  };
};
